'use client';

import React, { useEffect, useState, useRef, useMemo } from 'react';

import { cn } from '@app/libs/utils';
import { administrativeService } from '@app/services/administrative.service';

import ArrowDownIcon from '../icons/ArrowDownIcon';
import PlusIcon from '../icons/PlusIcon';
import Checkbox from '../ui/Checkbox';

interface AdministrativeSelectProps {
  provinceCodes: string[];
  wardCodes: string[];
  onChange: (...args: any[]) => void;
  className?: string;
  dropDownclassName?: string;
  triggerClassName?: string;
  children?: React.ReactNode;
}

export const AdministrativeSelect: React.FC<AdministrativeSelectProps> = ({
  provinceCodes,
  wardCodes,
  onChange,
  className,
  dropDownclassName = '',
  children,
  triggerClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>([]);
  const [selectedWards, setSelectedWards] = useState<string[]>([]);
  const [provinces, setProvinces] = useState<any[]>([]);
  const [wards, setWards] = useState<{ [key: string]: any[] }>({});
  const [openProvince, setOpenProvince] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const initWard = useRef<boolean>(true);
  const initProvince = useRef<boolean>(true);
  const [keyword, setKeyword] = useState<string>('');

  const nestedProvinces = useMemo(() => {
    return [
      ...new Set(
        Object.values(wards)
          .flat()
          .filter(ward => selectedWards.includes(ward.wardCode.toString()))
          .map(ward => ward.provinceCode.toString())
      ),
    ];
  }, [wards, selectedWards]);
  // Sync with props only when they change externally
  useEffect(() => {
    if (provinceCodes === undefined) return;
    if (initProvince.current) {
      initProvince.current = false;
      setSelectedProvinces(prev => [...new Set([...prev, ...provinceCodes])]);
    }
  }, [provinceCodes]);

  useEffect(() => {
    if (wardCodes === undefined) return;
    if (initWard.current) {
      initWard.current = false;
      setSelectedWards([wardCodes].flat().filter(code => !!code));
    }
  }, [wardCodes]);

  useEffect(() => {
    setLoading(true);
    administrativeService
      .getAllAdministrativeUnits()
      .then(({ provinces, wards }) => {
        setProvinces(provinces);
        setWards(wards);
      })
      .finally(() => setLoading(false));
  }, []);

  // Call onChange when our internal state changes
  useEffect(() => {
    if (selectedProvinces.length === 1) {
      const province = provinces.find(
        province => province.provinceCode.toString() === selectedProvinces[0]
      );
      onChange(selectedProvinces, selectedWards, province?.name);
    } else if (selectedWards.length === 1) {
      const ward = Object.values(wards)
        .flat()
        .find(ward => ward.wardCode.toString() === selectedWards[0]);
      const province = provinces.find(
        province => province.provinceCode.toString() === ward?.provinceCode.toString()
      );
      onChange(selectedProvinces, selectedWards, `${ward?.name}, ${province?.name}`);
    } else {
      onChange(selectedProvinces, selectedWards, '');
    }
  }, [selectedProvinces, selectedWards, onChange, provinces, wards]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as any;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    // Use capture phase to ensure we catch the event before other handlers
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Utility function to remove Vietnamese diacritics
  const removeVietnameseDiacritics = (str: string): string => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .replace(/ /g, '');
  };

  const filteredProvinces = useMemo(() => {
    if (!keyword) return { provinces, wards };

    const normalizedKeyword = removeVietnameseDiacritics(keyword.toLowerCase());

    const nWards = Object.values(wards)
      .flat()
      .filter(ward => {
        const normalizedWardName = removeVietnameseDiacritics(
          ward.name.replace(/Phường, Xã/, '').toLowerCase()
        );
        return normalizedWardName.includes(normalizedKeyword);
      });

    const fWards = nWards.reduce(
      (acc, ward) => {
        acc[ward.provinceCode] = [...(acc[ward.provinceCode] || []), ward];
        return acc;
      },
      {} as { [key: string]: any[] }
    );

    const ffWards: { [key: string]: any[] } = {};

    const fProvinceCodes = [...new Set(nWards.map(ward => ward.provinceCode))];

    const fProvinces = provinces.filter(province => {
      if (fProvinceCodes.includes(province.provinceCode)) {
        ffWards[province.provinceCode] = fWards[province.provinceCode] || [];

        return true;
      }

      const normalizedProvinceName = removeVietnameseDiacritics(
        province.name.replace(/Tỉnh, Thành phố/, '').toLowerCase()
      );
      if (normalizedProvinceName.includes(normalizedKeyword)) {
        ffWards[province.provinceCode] = fWards[province.provinceCode] || [];

        return true;
      }

      return false;
    });

    return { provinces: fProvinces, wards: ffWards };
  }, [keyword, provinces, wards]);

  return (
    <div
      ref={dropdownRef}
      className={cn('flex flex-col gap-2 justify-center relative select-none', className)}
    >
      <div
        className={cn('flex gap-2 group min-w-full min-h-full', triggerClassName, isOpen && 'open')}
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsOpen(!isOpen);
          }
        }}
      >
        {children || (
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 grow justify-between">
            <button className="text-xs flex items-center flex-nowrap text-gray-700 min-w-20">
              {selectedWards?.length + selectedProvinces?.length > 0
                ? `Khu vực (${selectedWards?.length + selectedProvinces?.length})`
                : 'Toàn quốc'}
            </button>
            <ArrowDownIcon className="w-4 h-4 group-[.open]:rotate-180 transition-transform text-gray-700" />
          </div>
        )}
      </div>
      {isOpen && (
        <div
          className={cn(
            'absolute top-full left-0 w-full bg-white border border-gray-200 rounded-xl shadow-lg z-1000 p-5 overflow-y-auto mt-1',
            dropDownclassName,
            !dropDownclassName.includes('w-') && 'w-120',
            !dropDownclassName.includes('max-h-') && 'max-h-100',
            isOpen && 'open'
          )}
        >
          {loading && (
            <div className="flex justify-center items-center h-full">Đang tải dữ liệu...</div>
          )}
          {!loading && (
            <ul className="flex flex-col gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Nhập từ khóa để tìm kiếm khu vực"
                  className="w-full px-3 py-2 h-9 border border-gray-200 rounded-md text-sm outline-none h-12 relative z-1"
                  value={keyword}
                  onChange={e => setKeyword(e.target.value)}
                />
                {keyword && (
                  <button
                    className="absolute right-0 top-0 h-full w-10 flex items-center justify-center cursor-pointer z-2"
                    onClick={() => setKeyword('')}
                  >
                    <PlusIcon className="w-4 h-4 text-gray-700 rotate-45" />
                  </button>
                )}
              </div>
              {!keyword && (
                <li
                  key="all"
                  className="cursor-pointer hover:bg-gray-100 rounded-lg p-2 flex items-center gap-2 group"
                >
                  <Checkbox
                    checked={selectedWards?.length + selectedProvinces?.length === 0}
                    onChange={() => {
                      setSelectedProvinces([]);
                      setSelectedWards([]);
                    }}
                    label="Toàn quốc"
                  />
                </li>
              )}
              {filteredProvinces?.provinces?.map(province => (
                <React.Fragment key={province.id}>
                  <li
                    className={cn(
                      'cursor-pointer hover:bg-gray-100 rounded-lg p-2 flex items-center gap-2 group',
                      openProvince[province.provinceCode] && 'open',
                      nestedProvinces.includes(province.provinceCode.toString()) && 'bg-gray-300'
                    )}
                    onClick={() =>
                      setOpenProvince({
                        ...openProvince,
                        [province.provinceCode]: !openProvince[province.provinceCode],
                      })
                    }
                  >
                    <Checkbox
                      checked={selectedProvinces?.includes(province.provinceCode.toString())}
                      onChange={() =>
                        setSelectedProvinces(prev => {
                          if (prev.includes(province.provinceCode.toString())) {
                            return prev.filter(code => code !== province.provinceCode.toString());
                          }
                          const wardCodes = (wards[province.provinceCode] || []).map(ward =>
                            ward.wardCode.toString()
                          );
                          setSelectedWards(prev =>
                            prev.flat().filter(code => !wardCodes.includes(code.toString()))
                          );

                          return [
                            ...new Set([...selectedProvinces, province.provinceCode.toString()]),
                          ];
                        })
                      }
                    />
                    {province.name}

                    <ArrowDownIcon className="w-4 h-4 transition-transform text-gray-700 group-[.open]:rotate-180" />
                  </li>

                  {openProvince[province.provinceCode] === true && (
                    <ul className="flex flex-col gap-2 ml-5">
                      {filteredProvinces?.wards[province.provinceCode]?.map(ward => (
                        <li
                          key={ward.id}
                          className="cursor-pointer hover:bg-gray-100 rounded-lg p-2 flex items-center gap-2"
                        >
                          <Checkbox
                            checked={selectedWards?.includes(ward.wardCode.toString())}
                            label={ward.name}
                            onChange={() =>
                              setSelectedWards(prev => {
                                if (prev.includes(ward.wardCode.toString())) {
                                  return prev.filter(code => code !== ward.wardCode.toString());
                                }

                                setSelectedProvinces(prev =>
                                  prev.filter(code => code !== ward.provinceCode.toString())
                                );

                                return [...new Set([...selectedWards, ward.wardCode.toString()])];
                              })
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </React.Fragment>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
